// ============================================================
// DEFENCE CALC - Apply defender custom modifiers to dodge/parry.
// Pure function, no React dependency.
// ============================================================
import type { DefenderCustomMod } from '../types';

export interface EffectiveDefence {
  effectiveDodge: number;
  effectiveParry: number;
}

/**
 * Apply defender custom modifiers to base dodge/parry skill values.
 * Each modifier's value is added to both dodge and parry totals.
 */
export function applyDefenderMods(baseDodge: number, baseParry: number, mods: DefenderCustomMod[]): EffectiveDefence {
  const total = mods.reduce((sum, m) => sum + m.value, 0);
  return {
    effectiveDodge: baseDodge + total,
    effectiveParry: baseParry + total,
  };
}

/**
 * The same target, with its custom defender modifiers folded into the
 * dodge and parry it will actually roll.
 *
 * `defenceProbability` has always done this at its own entry point and
 * traced the chain against the result. The LIVE path never did: neither
 * `handleDefenceChoice` nor `getAvailableDefenceSteps` reads
 * `defenderCustomMods`, and `projectDefender` deliberately does not fold
 * them in. So a defender modifier appeared in the forecast and never in
 * the roll.
 *
 * IDEMPOTENT BY CONSTRUCTION: the mods are emptied as they are folded, so
 * calling this twice cannot double them. That matters because the
 * forecast pre-folds and the live path folds at a different point, and a
 * value that silently doubles when two code paths meet is exactly the
 * kind of bug this function exists to end.
 */
export function withDefenderMods<
  T extends {
    dodge?: { agility?: number };
    parry?: { weaponSkill?: number };
    defenderCustomMods?: DefenderCustomMod[];
  },
>(target: T | null | undefined): T | null | undefined {
  if (!target) return target;
  const mods = target.defenderCustomMods ?? [];
  if (mods.length === 0) return target;
  const { effectiveDodge, effectiveParry } = applyDefenderMods(
    target.dodge?.agility ?? 0,
    target.parry?.weaponSkill ?? 0,
    mods,
  );
  return {
    ...target,
    dodge: { ...target.dodge, agility: effectiveDodge },
    parry: { ...target.parry, weaponSkill: effectiveParry },
    defenderCustomMods: [],
  };
}
